import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, Sparkles, ArrowLeft } from 'lucide-react';

// Import existing pages
import Scheduler from '@/pages/Scheduler';
import Community from '@/pages/Community';
import OptimizeContent from '@/pages/new-app/OptimizeContent';

export default function Publish() {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'scheduler');

  // Optional project/asset context arriving from Project Overview or
  // Project Upload ("Schedule" action) — stable ContentSource ids, not
  // filenames. When present, OptimizeContent seeds these as real,
  // selectable entries (in addition to whatever it already has) so the
  // existing optimize -> schedule flow starts from the real uploaded
  // asset(s) instead of requiring the user to find them in an unrelated
  // global list.
  const assetIdParam = searchParams.get('assetId');
  const projectId = searchParams.get('projectId');
  const creatorId = searchParams.get('creatorId');
  const title = searchParams.get('title');
  const prefilledAssets = assetIdParam
    ? assetIdParam.split(',').filter(Boolean).map((id) => ({ id, creatorId, title: title || undefined }))
    : [];

  return (
    <div className="min-h-screen bg-[#121417]">
      <div className="container mx-auto p-6 max-w-7xl">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-[#8A93A6] mb-2">
            <Link to="/app/create" className="hover:text-white">CREATE</Link>
            <span>/</span>
            {projectId ? (
              <>
                <Link to="/app/create/workspace" className="hover:text-white">Workspace</Link>
                <span>/</span>
                <Link to={`/app/create/workspace/project/${projectId}`} className="hover:text-white" data-testid="publish-back-to-project-link">Project</Link>
                <span>/</span>
              </>
            ) : null}
            <span className="text-white">Publish</span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Publish (Legacy)</h1>
          <p className="text-[#8A93A6]">This page is kept for compatibility with older links. The current Production Tools are <Link to={`/app/create/optimize?${projectId ? `projectId=${projectId}&creatorId=${searchParams.get('creatorId') || ''}` : ''}`} className="text-[#3A7BFF] hover:underline" data-testid="publish-go-to-optimize-link">Optimize</Link> and <Link to={`/app/create/schedule?${projectId ? `projectId=${projectId}&creatorId=${searchParams.get('creatorId') || ''}` : ''}`} className="text-[#3A7BFF] hover:underline" data-testid="publish-go-to-schedule-link">Schedule</Link>.</p>
          <div className="flex items-center gap-4 mt-2 flex-wrap">
            {projectId && (
              <Link
                to={`/app/create/workspace/project/${projectId}`}
                className="inline-flex items-center gap-1.5 text-xs text-[#8A93A6] hover:text-white"
                data-testid="publish-back-to-project-button"
              >
                <ArrowLeft className="h-3 w-3" />
                Back to Project
              </Link>
            )}
            <Link
              to={`/app/create/content-studio?${projectId ? `projectId=${projectId}&` : ''}returnTo=${encodeURIComponent(`/app/create/publish?${searchParams.toString()}`)}`}
              className="inline-flex items-center gap-1.5 text-xs text-[#8A93A6] hover:text-white"
              data-testid="publish-media-library-link"
            >
              Media Library
            </Link>
          </div>
        </div>

        {/* Tabs Navigation — Community intentionally hidden from this
            visible list (section 3 of the earlier Optimize spec): its
            code, import and TabsContent below are kept exactly as-is,
            just unreachable from the UI, so nothing is deleted.
            "Schedule (Legacy)" and "Optimization (Legacy)" are kept as
            fallback paths for old links — the dedicated workspaces now
            live at /app/create/optimize and /app/create/schedule
            (section 2: Scheduler was renamed to Schedule there). */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="glass mb-6 w-full justify-start">
            <TabsTrigger value="scheduler" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Schedule (Legacy)
            </TabsTrigger>
            <TabsTrigger value="optimization" className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              Optimization (Legacy)
            </TabsTrigger>
          </TabsList>

          {/* Tab Content */}
          <TabsContent value="optimization">
            <OptimizeContent prefilledAssets={prefilledAssets} />
          </TabsContent>

          <TabsContent value="scheduler">
            <Scheduler />
          </TabsContent>

          <TabsContent value="community">
            <Community />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
